/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { Fragment, useEffect, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ScorecardIcon from '@atlaskit/icon/core/scorecard';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import TaskToDoIcon from '@atlaskit/icon/core/task-to-do';
import VideoStopOverlayIcon from '@atlaskit/icon/core/video-stop-overlay';
import IconTile from '@atlaskit/icon/icon-tile';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import type {
	ExecutionSegment,
	TodoSnapshot,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { getAppearanceFromId } from '@atlassian/conversation-assistant-utils/icon-tile-utils';
import { RainbowProgressBar } from '@atlassian/rovo-platform-ui-components/primitives/RainbowProgressBar';

import messages from './messages';

const formatElapsedTime = (seconds: number): string => {
	const mins = Math.floor(seconds / 60);
	const secs = seconds % 60;
	if (mins === 0) {
		return `${secs}s`;
	}
	return `${mins}m ${secs}s`;
};

/**
 * Computes the total elapsed seconds from execution segments.
 *
 * Segments are parsed to epoch-ms once, sorted by start time, and their
 * durations summed to produce a base elapsed value. Open-ended segments
 * (no `endedAt`) use `Date.now()` for their end boundary.
 *
 * When `isPaused` is false the counter ticks every second (+1) from the
 * computed base. When `isPaused` is true the display freezes at the base.
 *
 * Falls back to a simple wall-clock counter when no segments are provided.
 */
const computeBaseSeconds = (segments: ExecutionSegment[]): number => {
	const parsed = segments
		.map((seg) => ({
			start: new Date(seg.startedAt).getTime(),
			end: seg.endedAt ? new Date(seg.endedAt).getTime() : Date.now(),
		}))
		.sort((a, b) => a.start - b.start);

	let totalMs = 0;
	for (const { start, end } of parsed) {
		totalMs += Math.max(0, end - start);
	}
	return Math.floor(totalMs / 1000);
};

const useElapsedSeconds = (
	executionSegments?: ExecutionSegment[] | null,
	isPaused?: boolean,
): number => {
	const hasSegments = executionSegments && executionSegments.length > 0;
	const [elapsedSeconds, setElapsedSeconds] = useState(() =>
		hasSegments ? computeBaseSeconds(executionSegments) : 0,
	);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// When segments change, recompute the base elapsed time
	useEffect(() => {
		if (hasSegments) {
			setElapsedSeconds(computeBaseSeconds(executionSegments));
		}
	}, [executionSegments, hasSegments]);

	// Manage the ticking interval — cancel and early-return when paused
	useEffect(() => {
		if (isPaused) {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			return;
		}

		intervalRef.current = setInterval(() => {
			setElapsedSeconds((s) => s + 1);
		}, 1000);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [isPaused]);

	return elapsedSeconds;
};

/**
 * Computes a progress percentage (0–95) from a todo snapshot.
 * Progress never goes backwards — the returned value is the max of the
 * previously computed value and the newly derived one.
 */
const useTaskProgress = (todoSnapshot?: TodoSnapshot): number => {
	const progressRef = useRef(10);
	const [progress, setProgress] = useState(10);

	useEffect(() => {
		let computed: number;
		if (!todoSnapshot || todoSnapshot.todos.length === 0) {
			computed = 10;
		} else {
			const total = todoSnapshot.todos.length;
			const completed = todoSnapshot.todos.filter((t) => t.status === 'completed').length;
			computed = Math.max(10, Math.min(95, Math.round((completed / total) * 95)));
		}
		const next = Math.max(progressRef.current, computed);
		if (next !== progressRef.current) {
			progressRef.current = next;
			setProgress(next);
		}
	}, [todoSnapshot]);

	return progress;
};

// ── Layout ──────────────────────────────────────────────────────────────

const styles = cssMap({
	shadowWrapper: {
		// `borderRadius` ensures the shadow follows the same rounded corners as the inner container.
		borderRadius: token('radius.xlarge'),
		// @ts-expect-error - raw boxShadow value has no design token equivalent — custom shadow required by design spec
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-unsafe-values
		boxShadow: '0 -2px 50px 8px rgba(30, 31, 33, 0.08)',
	},
	container: {
		display: 'flex',
		flexDirection: 'column',
		borderRadius: token('radius.xlarge'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		overflow: 'clip',
		width: '100%',
		backgroundColor: token('elevation.surface'),
	},
	header: {
		display: 'flex',
		gap: token('space.150'),
		alignItems: 'center',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	headerBorder: {
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderColor: token('color.border'),
	},
	titleArea: {
		display: 'flex',
		flex: '1 0 0',
		flexDirection: 'column',
		gap: token('space.050'),
		justifyContent: 'center',
		minWidth: '0px',
	},
	// ── Todo list ────────────────────────────────────────────────────
	todoContainer: {
		overflow: 'hidden',
		transition: 'max-height 300ms ease-in-out, opacity 300ms ease-in-out',
	},
	todoExpanded: {
		maxHeight: '500px',
		opacity: 1,
	},
	todoCollapsed: {
		maxHeight: '0px',
		opacity: 0,
	},
	todoInner: {
		display: 'flex',
		flexDirection: 'column',
		paddingInline: token('space.200'),
		paddingBlock: token('space.150'),
	},
	todoRow: {
		display: 'flex',
		gap: token('space.150'),
		alignItems: 'center',
		width: '100%',
	},
	todoIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
	},
	todoTextBase: {
		flex: '1 0 0',
		minWidth: '0px',
	},
	connector: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '8px',
	},
	connectorLine: {
		width: '1px',
		height: '8px',
		backgroundColor: token('color.background.neutral'),
	},
	elapsedTime: {
		whiteSpace: 'nowrap',
		flexShrink: 0,
	},
	todoList: {
		listStyle: 'none',
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
});

// ── Sub-components ──────────────────────────────────────────────────────

type TodoStatusIconProps = {
	status: string;
	inProgressLabel: string;
};

const TodoStatusIcon = ({ status, inProgressLabel }: TodoStatusIconProps) => {
	switch (status) {
		case 'completed':
			return <StatusSuccessIcon label="" color={token('color.icon.success')} />;
		case 'in_progress':
			return <Spinner size={16} label={inProgressLabel} />;
		case 'pending':
		default:
			return <TaskToDoIcon label="" color={token('color.icon.subtle')} />;
	}
};

// ── Main component ──────────────────────────────────────────────────────

export type TaskExecutingViewProps = {
	todoSnapshot?: TodoSnapshot;
	title?: string;
	executionSegments?: ExecutionSegment[] | null;
	isPaused?: boolean;
	onStopTask?: () => void;
};

export const TaskExecutingView = ({
	todoSnapshot,
	title,
	executionSegments,
	isPaused,
	onStopTask,
}: TaskExecutingViewProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const elapsedSeconds = useElapsedSeconds(executionSegments, isPaused);
	const progress = useTaskProgress(todoSnapshot);

	const hasTodos = !!(todoSnapshot && todoSnapshot.todos.length > 0);
	const taskTitle = title || formatMessage(messages.defaultTitle);
	const elapsedTimeFormatted = formatElapsedTime(elapsedSeconds);

	return (
		<div css={styles.shadowWrapper}>
			<div css={styles.container}>
				<div css={[styles.header, hasTodos && styles.headerBorder]}>
					<IconTile
						icon={ScorecardIcon}
						label={taskTitle}
						appearance={getAppearanceFromId(taskTitle)}
						size="medium"
					/>
					<div css={styles.titleArea}>
						<Inline alignBlock="center" space="space.050">
							<Text weight="semibold" maxLines={1} as="span">
								{taskTitle}
							</Text>
							<Text size="small" color="color.text.subtlest" as="span">
								•
							</Text>
							<Box
								xcss={styles.elapsedTime}
								aria-label={formatMessage(messages.taskElapsedTimeLabel, {
									time: elapsedTimeFormatted,
								})}
								aria-live="polite"
								aria-atomic="true"
							>
								<Text size="small" color="color.text.subtlest" as="span" aria-hidden>
									{elapsedTimeFormatted}
								</Text>
							</Box>
						</Inline>
						<RainbowProgressBar
							status="running"
							label={formatMessage(messages.progressBarLabel)}
							progress={progress}
						/>
					</div>
					<IconButton
						appearance="default"
						onClick={onStopTask}
						icon={(iconProps) => (
							<VideoStopOverlayIcon {...iconProps} color={token('color.icon.danger')} label="" />
						)}
						label={formatMessage(messages.stopTaskLabel)}
					/>
				</div>

				<div css={[styles.todoContainer, hasTodos ? styles.todoExpanded : styles.todoCollapsed]}>
					<div css={styles.todoInner}>
						{hasTodos && (
							<ul aria-label={formatMessage(messages.todoListLabel)} css={styles.todoList}>
								{todoSnapshot!.todos.map((todo, index) => (
									<Fragment key={todo.id}>
										<li css={styles.todoRow}>
											<div css={styles.todoIcon}>
												<TodoStatusIcon
													status={todo.status}
													inProgressLabel={formatMessage(messages.todoStepInProgressLabel)}
												/>
											</div>
											<Box xcss={styles.todoTextBase}>
												<Text
													color={todo.status === 'completed' ? 'color.text.disabled' : 'color.text'}
												>
													{todo.content}
												</Text>
											</Box>
										</li>
										{index < todoSnapshot!.todos.length - 1 && (
											<li css={styles.connector} aria-hidden="true">
												<div css={styles.connectorLine} />
											</li>
										)}
									</Fragment>
								))}
							</ul>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
